/**
 * Container for the Sign action modal.
 * Manages transfer fee, annual salary, and duration draft state, and feeds
 * those terms to the Live Impact verdict as they are typed (SE-7002).
 * @module features/squad-planning/components/SignActionModal
 */

import { type Href, useRouter } from "expo-router";
import React, { memo, useCallback, useMemo, useState } from "react";

import { POSTHOG_FLAGS } from "@/config/featureFlags";
import usePostHogFeatureFlag from "@/hooks/usePostHogFeatureFlag";
import { buildComplianceHref } from "@/features/financial-compliance/navigation/complianceRoute";

import { useLiveImpact } from "../../../hooks/useLiveImpact";

import SignActionModalView from "./SignActionModalView";

import type { ActionTerms } from "@/features/financial-compliance/domain/actionFinancials";

/** Props for {@link SignActionModalContainer} */
export interface SignActionModalProps {
  /** Whether the modal is open */
  readonly isOpen: boolean;
  /** Called to close the modal */
  readonly onClose: () => void;
  /** Called with all financial details on confirm */
  readonly onConfirm: (
    transferFee: number | null,
    annualSalary: number | null,
    durationMonths: number | null,
    signingBonus: number | null,
    agentFee: number | null
  ) => void;
  /** Called to remove the action (edit mode only) */
  readonly onCancelAction?: () => void;
  /** Player name for the title */
  readonly playerName: string;
  /**
   * Player the action applies to.
   *
   * Optional so no mount point breaks by omitting it; the Live Impact verdict
   * uses it to avoid double-counting a player who is already staged.
   */
  readonly playerId?: string | null;
  /** Initial transfer fee value (for edit mode) */
  readonly initialTransferFee?: number | null;
  /** Initial annual salary value (for edit mode) */
  readonly initialAnnualSalary?: number | null;
  /** Initial duration in months (for edit mode) */
  readonly initialDuration?: number | null;
  /** Initial signing bonus value (for edit mode) */
  readonly initialSigningBonus?: number | null;
  /** Initial agent fee value (for edit mode) */
  readonly initialAgentFee?: number | null;
  /** Whether the modal is in edit mode (existing action) */
  readonly isEditMode?: boolean;
}

/**
 * Container managing Sign action modal state.
 * @param root0 - Component props
 * @param root0.isOpen - Whether modal is visible
 * @param root0.onClose - Close handler
 * @param root0.onConfirm - Confirm handler with transfer fee, salary, and duration
 * @param root0.onCancelAction - Remove action handler (edit mode)
 * @param root0.playerName - Player name for display
 * @param root0.playerId - Player the action applies to
 * @param root0.initialTransferFee - Initial transfer fee for edit mode
 * @param root0.initialAnnualSalary - Initial annual salary for edit mode
 * @param root0.initialDuration - Initial duration for edit mode
 * @param root0.initialSigningBonus - Initial signing bonus for edit mode
 * @param root0.initialAgentFee - Initial agent fee for edit mode
 * @param root0.isEditMode - Whether in edit mode
 */
const SignActionModalContainer = ({
  isOpen,
  onClose,
  onConfirm,
  onCancelAction,
  playerName,
  playerId = null,
  initialTransferFee = null,
  initialAnnualSalary = null,
  initialDuration = null,
  initialSigningBonus = null,
  initialAgentFee = null,
  isEditMode = false,
}: SignActionModalProps) => {
  const [transferFee, setTransferFee] = useState<number | null>(
    initialTransferFee
  );
  const [annualSalary, setAnnualSalary] = useState<number | null>(
    initialAnnualSalary
  );
  const [durationText, setDurationText] = useState(
    initialDuration != null ? String(initialDuration) : ""
  );
  const [signingBonus, setSigningBonus] = useState<number | null>(
    initialSigningBonus
  );
  const [agentFee, setAgentFee] = useState<number | null>(initialAgentFee);
  const router = useRouter();

  const terms = useMemo<ActionTerms>(
    () => ({
      kind: "Sign",
      transferFee,
      annualSalary,
      durationMonths: durationText !== "" ? Number(durationText) : null,
      signingBonus,
      agentFee,
    }),
    [transferFee, annualSalary, durationText, signingBonus, agentFee]
  );
  const { model: liveImpact, scenarioId } = useLiveImpact(terms, playerId);
  // SE-8003: the Compliance tab is unreachable with the flag off, so this
  // link must not navigate into it. `=== true` is the fail-safe idiom — an
  // unconfigured flag, a still-loading PostHog client, or any non-`true`
  // value all skip the navigation, never perform it.
  const isComplianceEnabled =
    usePostHogFeatureFlag(POSTHOG_FLAGS.SQUAD_FINANCIALS_TAB) === true;

  const handleViewFullImpact = useCallback(() => {
    onClose();
    if (isComplianceEnabled) {
      router.push(buildComplianceHref(scenarioId) as Href);
    }
  }, [router, scenarioId, onClose, isComplianceEnabled]);

  const handleTransferFeeChange = useCallback((value: number | null) => {
    setTransferFee(value);
  }, []);

  const handleAnnualSalaryChange = useCallback((value: number | null) => {
    setAnnualSalary(value);
  }, []);

  const handleDurationChange = useCallback((text: string) => {
    setDurationText(text.replace(/\D/g, ""));
  }, []);

  const handleSigningBonusChange = useCallback((value: number | null) => {
    setSigningBonus(value);
  }, []);

  const handleAgentFeeChange = useCallback((value: number | null) => {
    setAgentFee(value);
  }, []);

  const handleConfirm = useCallback(() => {
    const months = durationText !== "" ? parseInt(durationText, 10) : null;
    onConfirm(
      transferFee,
      annualSalary,
      Number.isNaN(months) ? null : months,
      signingBonus,
      agentFee
    );
  }, [
    transferFee,
    annualSalary,
    durationText,
    signingBonus,
    agentFee,
    onConfirm,
  ]);

  if (!isOpen) return null;

  return (
    <SignActionModalView
      isOpen={isOpen}
      playerName={playerName}
      transferFee={transferFee}
      annualSalary={annualSalary}
      durationText={durationText}
      signingBonus={signingBonus}
      agentFee={agentFee}
      onTransferFeeChange={handleTransferFeeChange}
      onAnnualSalaryChange={handleAnnualSalaryChange}
      onDurationChange={handleDurationChange}
      onSigningBonusChange={handleSigningBonusChange}
      onAgentFeeChange={handleAgentFeeChange}
      onConfirm={handleConfirm}
      onCancel={onClose}
      onCancelAction={onCancelAction}
      isEditMode={isEditMode}
      liveImpact={liveImpact}
      onViewFullImpact={handleViewFullImpact}
    />
  );
};

SignActionModalContainer.displayName = "SignActionModalContainer";

export default memo(SignActionModalContainer);
